import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../config/apiRouting';
import authenticatedFetch from '../utils/authenticatedFetch';
import { apiGet } from '../utils/apiGatewayMigration';
import './EquipmentAnalytics.css';

interface AnalyticsData {
  totalDevices: number;
  byManufacturer: Record<string, number>;
  byEquipmentType: Record<string, number>;
  byStatus: Record<string, number>;
  byConnectionStatus: Record<string, number>;
  healthScoreDistribution: {
    excellent: number; // 90-100
    good: number; // 70-89
    fair: number; // 50-69
    poor: number; // 0-49
  };
  alerts: {
    total: number;
    byStatus: Record<string, number>;
    bySeverity: Record<string, number>;
  };
  trends: {
    avgHealthScore: number;
    avgUptime: number;
    totalCookCycles: number;
    avgEnergyUsage: number;
  };
}

interface Store {
  storeId: string;
  storeName: string;
  enterpriseId: string;
}

interface Enterprise {
  enterpriseId: string;
  name: string;
}

const EquipmentAnalytics: React.FC = () => {
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState<string>('');
  const [selectedStore, setSelectedStore] = useState<string>('all');
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load enterprises on mount
  useEffect(() => {
    loadEnterprises();
  }, []);

  // Load stores when enterprise changes
  useEffect(() => {
    if (selectedEnterprise) {
      loadStores(selectedEnterprise);
    } else {
      setStores([]);
      setSelectedStore('all');
    }
  }, [selectedEnterprise]);

  // Load analytics data when filters change
  useEffect(() => {
    if (selectedEnterprise) {
      fetchAnalytics();
    }
  }, [selectedEnterprise, selectedStore]);

  const loadEnterprises = async () => {
    try {
      const data = await apiGet('enterprises');
      const enterprisesList = Array.isArray(data) ? data : (data.data || []);
      setEnterprises(enterprisesList);

      if (enterprisesList.length > 0) {
        setSelectedEnterprise(enterprisesList[0].enterpriseId);
      }
    } catch (error) {
      console.error('Error loading enterprises:', error);
      setError('Failed to load enterprises');
    }
  };

  const loadStores = async (enterpriseId: string) => {
    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINTS.stores()}?enterpriseId=${enterpriseId}`
      );
      if (response.ok) {
        const data = await response.json();
        const storesList = data.success ? data.data : data;
        setStores(Array.isArray(storesList) ? storesList : []);
      }
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch devices
      const devicesUrl = selectedStore === 'all'
        ? API_ENDPOINTS.equipmentDevices({ enterpriseId: selectedEnterprise })
        : API_ENDPOINTS.equipmentDevices({ storeId: selectedStore });

      const devicesResponse = await authenticatedFetch(devicesUrl);
      if (!devicesResponse.ok) throw new Error('Failed to fetch devices');

      const devicesData = await devicesResponse.json();
      const devices = devicesData.success ? devicesData.data.devices : devicesData.devices || [];

      // Fetch alerts
      const alertsUrl = selectedStore === 'all'
        ? `${API_ENDPOINTS.equipmentAlerts()}?enterpriseId=${selectedEnterprise}`
        : API_ENDPOINTS.equipmentAlerts({ storeId: selectedStore });

      let alerts = [];
      try {
        const alertsResponse = await authenticatedFetch(alertsUrl);
        if (alertsResponse.ok) {
          const alertsData = await alertsResponse.json();
          alerts = alertsData.success ? alertsData.data.alerts : alertsData.alerts || [];
        }
      } catch (err) {
        console.warn('Could not fetch alerts:', err);
      }

      // Fetch metrics for all devices
      const metricsPromises = devices.map(async (device: any) => {
        try {
          const response = await authenticatedFetch(
            `${API_ENDPOINTS.equipmentMetrics(device.deviceId)}?days=30`
          );
          if (response.ok) {
            const data = await response.json();
            return data.success ? data.data.metrics : data.metrics || [];
          }
        } catch (err) {
          console.warn(`Could not fetch metrics for ${device.deviceId}:`, err);
        }
        return [];
      });

      const allMetrics = await Promise.all(metricsPromises);
      const flatMetrics = allMetrics.flat();

      // Process analytics
      const analyticsData = processAnalytics(devices, alerts, flatMetrics);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const processAnalytics = (devices: any[], alerts: any[], metrics: any[]): AnalyticsData => {
    const analytics: AnalyticsData = {
      totalDevices: devices.length,
      byManufacturer: {},
      byEquipmentType: {},
      byStatus: {},
      byConnectionStatus: {},
      healthScoreDistribution: {
        excellent: 0,
        good: 0,
        fair: 0,
        poor: 0
      },
      alerts: {
        total: alerts.length,
        byStatus: {},
        bySeverity: {}
      },
      trends: {
        avgHealthScore: 0,
        avgUptime: 0,
        totalCookCycles: 0,
        avgEnergyUsage: 0
      }
    };

    // Process devices
    devices.forEach(device => {
      // Manufacturer
      analytics.byManufacturer[device.manufacturer] =
        (analytics.byManufacturer[device.manufacturer] || 0) + 1;

      // Equipment type
      analytics.byEquipmentType[device.equipmentType] =
        (analytics.byEquipmentType[device.equipmentType] || 0) + 1;

      // Status
      analytics.byStatus[device.status] =
        (analytics.byStatus[device.status] || 0) + 1;

      // Connection status
      analytics.byConnectionStatus[device.connectionStatus] =
        (analytics.byConnectionStatus[device.connectionStatus] || 0) + 1;

      // Health score distribution
      const health = device.healthScore || 0;
      if (health >= 90) analytics.healthScoreDistribution.excellent++;
      else if (health >= 70) analytics.healthScoreDistribution.good++;
      else if (health >= 50) analytics.healthScoreDistribution.fair++;
      else analytics.healthScoreDistribution.poor++;
    });

    // Process alerts
    alerts.forEach(alert => {
      analytics.alerts.byStatus[alert.status] =
        (analytics.alerts.byStatus[alert.status] || 0) + 1;

      analytics.alerts.bySeverity[alert.severity] =
        (analytics.alerts.bySeverity[alert.severity] || 0) + 1;
    });

    // Process metrics for trends
    if (metrics.length > 0) {
      const totalHealthScore = metrics.reduce((sum, m) => sum + (m.metrics?.healthScore || 0), 0);
      const totalUptime = metrics.reduce((sum, m) => sum + (m.metrics?.uptimePercentage || 0), 0);
      const totalCookCycles = metrics.reduce((sum, m) => sum + (m.metrics?.cookCycles || 0), 0);
      const totalEnergy = metrics.reduce((sum, m) => sum + (m.metrics?.energyUsage || 0), 0);

      analytics.trends.avgHealthScore = Math.round(totalHealthScore / metrics.length);
      analytics.trends.avgUptime = Math.round(totalUptime / metrics.length);
      analytics.trends.totalCookCycles = totalCookCycles;
      analytics.trends.avgEnergyUsage = Math.round(totalEnergy / metrics.length);
    }

    return analytics;
  };

  const renderBarChart = (data: Record<string, number>, title: string, colorClass: string = 'bar-default') => {
    const maxValue = Math.max(...Object.values(data), 1);
    const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);

    return (
      <div className="chart-container">
        <h3>{title}</h3>
        <div className="bar-chart">
          {entries.map(([key, value]) => (
            <div key={key} className="bar-item">
              <div className="bar-label">{key}</div>
              <div className="bar-wrapper">
                <div
                  className={`bar ${colorClass}`}
                  style={{ width: `${(value / maxValue) * 100}%` }}
                >
                  <span className="bar-value">{value}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPieChart = (data: Record<string, number>, title: string) => {
    const total = Object.values(data).reduce((sum, val) => sum + val, 0);
    const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
    const colors = ['#4CAF50', '#2196F3', '#FFC107', '#F44336', '#9C27B0'];

    return (
      <div className="chart-container">
        <h3>{title}</h3>
        <div className="pie-chart-legend">
          {entries.map(([key, value], index) => (
            <div key={key} className="legend-item">
              <span
                className="legend-color"
                style={{ backgroundColor: colors[index % colors.length] }}
              />
              <span className="legend-label">{key}</span>
              <span className="legend-value">
                {value} ({Math.round((value / total) * 100)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="equipment-analytics">
        <div className="analytics-header">
          <h1>Equipment Analytics</h1>
        </div>
        <div className="loading-message">Loading analytics data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="equipment-analytics">
        <div className="analytics-header">
          <h1>Equipment Analytics</h1>
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="equipment-analytics">
      <div className="analytics-header">
        <h1>Equipment Analytics</h1>
        <div className="filters">
          <div className="filter-group">
            <label>Enterprise:</label>
            <select
              value={selectedEnterprise}
              onChange={(e) => setSelectedEnterprise(e.target.value)}
              className="filter-select"
            >
              <option value="">Select Enterprise</option>
              {enterprises.map(ent => (
                <option key={ent.enterpriseId} value={ent.enterpriseId}>
                  {ent.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Store:</label>
            <select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="filter-select"
              disabled={!selectedEnterprise}
            >
              <option value="all">All Stores</option>
              {stores.map(store => (
                <option key={store.storeId} value={store.storeId}>
                  {store.storeName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {analytics && (
        <>
          {/* Key Metrics Summary */}
          <div className="metrics-summary">
            <div className="metric-card">
              <h3>Total Devices</h3>
              <div className="metric-value">{analytics.totalDevices}</div>
            </div>
            <div className="metric-card">
              <h3>Avg Health Score</h3>
              <div className="metric-value">{analytics.trends.avgHealthScore}%</div>
            </div>
            <div className="metric-card">
              <h3>Avg Uptime</h3>
              <div className="metric-value">{analytics.trends.avgUptime}%</div>
            </div>
            <div className="metric-card">
              <h3>Total Alerts</h3>
              <div className="metric-value">{analytics.alerts.total}</div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="charts-grid">
            {renderBarChart(analytics.byManufacturer, 'Devices by Manufacturer', 'bar-manufacturer')}
            {renderBarChart(analytics.byEquipmentType, 'Devices by Equipment Type', 'bar-type')}
            {renderPieChart(analytics.byStatus, 'Device Status Distribution')}
            {renderPieChart(analytics.byConnectionStatus, 'Connection Status')}
          </div>

          {/* Health Score Distribution */}
          <div className="health-distribution">
            <h2>Health Score Distribution</h2>
            <div className="health-grid">
              <div className="health-card excellent">
                <div className="health-label">Excellent (90-100)</div>
                <div className="health-count">{analytics.healthScoreDistribution.excellent}</div>
              </div>
              <div className="health-card good">
                <div className="health-label">Good (70-89)</div>
                <div className="health-count">{analytics.healthScoreDistribution.good}</div>
              </div>
              <div className="health-card fair">
                <div className="health-label">Fair (50-69)</div>
                <div className="health-count">{analytics.healthScoreDistribution.fair}</div>
              </div>
              <div className="health-card poor">
                <div className="health-label">Poor (0-49)</div>
                <div className="health-count">{analytics.healthScoreDistribution.poor}</div>
              </div>
            </div>
          </div>

          {/* Alerts Summary */}
          {analytics.alerts.total > 0 && (
            <div className="charts-grid">
              {renderBarChart(analytics.alerts.bySeverity, 'Alerts by Severity', 'bar-severity')}
              {renderBarChart(analytics.alerts.byStatus, 'Alerts by Status', 'bar-status')}
            </div>
          )}

          {/* Operational Trends */}
          <div className="trends-section">
            <h2>Operational Trends (Last 30 Days)</h2>
            <div className="trends-grid">
              <div className="trend-card">
                <h3>Total Cook Cycles</h3>
                <div className="trend-value">{analytics.trends.totalCookCycles.toLocaleString()}</div>
              </div>
              <div className="trend-card">
                <h3>Avg Energy Usage</h3>
                <div className="trend-value">{analytics.trends.avgEnergyUsage} kWh</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EquipmentAnalytics;
